/**
 * Ops login page — clean, elegant, brand-aligned.
 * Route: /ops/login
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOpsAuth, formatAuthError } from './OpsAuthContext';
import brandLogo from '../assets/childhood-horizons-logo.jpeg';

const OpsLoginPage = () => {
  const { login, user } = useOpsAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const from = location.state?.from || '/ops';

  // Redirect already-authenticated users off the login page (in an effect so
  // we never call navigate() during render).
  useEffect(() => {
    if (user) navigate(from, { replace: true });
  }, [user, from, navigate]);

  if (user) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      dir="rtl"
      data-testid="ops-login-page"
      className="min-h-screen bg-[#F7F5F2] flex items-center justify-center px-4 py-12"
    >
      <div className="w-full max-w-md">
        <div className="bg-white rounded-[24px] shadow-[0_24px_60px_-24px_rgba(31,61,43,0.25)] ring-1 ring-[#E6E0D6] px-6 sm:px-8 py-10">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <img
              src={brandLogo}
              alt="Childhood Horizons"
              className="w-28 h-28 object-contain mb-3"
              style={{ mixBlendMode: 'multiply' }}
            />
            <h1 className="font-['Noto_Kufi_Arabic'] text-[24px] text-[#1F3D2B] font-bold tracking-tight">
              نظام التشغيل
            </h1>
            <p className="font-['Playfair_Display'] italic text-[13px] text-[#7A866E] mt-1">
              Internal Ops · Childhood Horizons
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="ops-email"
                className="block font-['Noto_Kufi_Arabic'] text-[13px] font-semibold text-[#2C3814] mb-2"
              >
                البريد الإلكتروني
              </label>
              <input
                id="ops-email"
                data-testid="ops-login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                dir="ltr"
                className="w-full text-start px-4 py-3 border border-[#D8D2C8] rounded-xl focus:border-[#4A5D23] focus:ring-2 focus:ring-[#4A5D23]/20 outline-none text-[#1F3D2B] font-['Noto_Kufi_Arabic']"
                placeholder="name@childhorizonsconsult.com"
              />
            </div>

            <div>
              <label
                htmlFor="ops-password"
                className="block font-['Noto_Kufi_Arabic'] text-[13px] font-semibold text-[#2C3814] mb-2"
              >
                كلمة المرور
              </label>
              <input
                id="ops-password"
                data-testid="ops-login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full text-start px-4 py-3 border border-[#D8D2C8] rounded-xl focus:border-[#4A5D23] focus:ring-2 focus:ring-[#4A5D23]/20 outline-none text-[#1F3D2B] font-['Noto_Kufi_Arabic']"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div
                data-testid="ops-login-error"
                className="text-[13px] text-[#B84A3A] bg-[#FBEDEA] border border-[#F3D6D0] rounded-lg px-3 py-2 font-['Noto_Kufi_Arabic']"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              data-testid="ops-login-submit"
              disabled={submitting}
              className="w-full bg-[#1F3D2B] text-white font-['Noto_Kufi_Arabic'] font-semibold text-sm py-3.5 rounded-xl hover:bg-[#163020] transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'جارٍ الدخول…' : 'الدخول إلى النظام'}
            </button>
          </form>

          <p className="mt-6 text-[11px] text-[#7A866E] text-center font-['Noto_Kufi_Arabic'] leading-6">
            نسيتِ كلمة المرور؟ تواصلي مع المدير التنفيذي
          </p>
        </div>

        <p className="mt-6 text-center text-[11px] text-[#7A866E] font-['Noto_Kufi_Arabic']">
          © Childhood Horizons — Internal Operating System
        </p>
      </div>
    </main>
  );
};

export default OpsLoginPage;
